import React, { useMemo, useState, forwardRef, useImperativeHandle, useRef, useContext, useEffect } from 'react';
import { Dropdown, Menu } from 'antd';
import { PaperIcon } from '@nackle/paper';
import { useTranslations } from '../../hooks/translations';
import { LoadingOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { NotificationCarousel } from '../../notifications/NotificationCarousel';
import './styles.less';
import { ACTIVE_THEME_NAME } from '../../branding';
import moment from 'moment';
import { BellFull, BellEmpty } from './Bell.js';
import { NotificationContext } from '../../notifications/NotificationsContext';
import { Translate } from '../../services';

export const NotificationDropdown = forwardRef( ( { onVisibleChange = () => {}, visible: _visible = false, loggingOut }, ref ) => {
    
    const { locale } = useTranslations();

    const carouselRef = useRef();

    const [ visible, setVisible ] = useState( _visible );

    const { notifications, loading, localRead } = useContext( NotificationContext );

    useEffect( () => setVisible( _visible ), [ _visible ] );

    const htmlDecode = ( input ) => {
        const doc = new DOMParser().parseFromString( input, 'text/html' );
        return doc.documentElement.textContent;
    };
    
    const unreadCount = useMemo( () => {
        const unreadList = ( notifications ?? [] )
            .filter( n => !n.isRead );
        return unreadList.length;
    }, [ notifications ] );

    useImperativeHandle( ref, () => ( {
        open: ( ) => {
            setVisible( true );
            // onVisibleChange( true );
        },
        close: () => {
            setVisible( false );
            // onVisibleChange( false );
        }
    } ) );

    useEffect( () => {
        moment.locale( locale.substring( 0, 2 ) );
    }, [ locale ] );

    function handleClick( noti ) {
        carouselRef.current?.open( noti.id );
    }

    /**
     * close dropdown when you click off of it (stay open on carousel click)
     * for some reason the other dropdowns seem to do this natively
     */
    useEffect( () => {
        const onClick = ( element ) => {
            const [ dropdownElem, dropdownButton, carouselElem ] = [
                document.getElementsByClassName( 'notification-dropdown' )?.[ 0 ],
                document.getElementsByClassName( 'notifications-button' )?.[ 0 ],
                document.getElementsByClassName( 'notifications-carousel' )?.[ 0 ]
            ];


            if ( ( !dropdownElem || !dropdownElem.contains( element.target ) ) &&
                ( !dropdownButton || !dropdownButton.contains( element.target ) ) &&
                ( !carouselElem || !carouselElem.contains( element.target ) ) ) {
                    setVisible( false );
            }
        };

        window.addEventListener( 'click', onClick );

        return () => {
            window.removeEventListener( 'click', onClick );
        };
    }, [] );

    const notiList = useMemo( _ => ( 
        <Menu className={ classNames( { loading } ) }>
            { !notifications  || loading
                ? <Menu.Item key='loading' className='loading'>
                    <LoadingOutlined className='secondary' />
                </Menu.Item>
            : notifications.length == 0
                ? <div className='no-content'>
                    <Translate.Text value='notification.dropdown.empty' />
                </div>
                : <>
                    <div className='action-bar'>
                        <span onClick={ () => setVisible( false ) }>
                            <PaperIcon icon='ChevronUp' collection='lindua' />
                            <Translate.Text value='button.collapse' />
                        </span>
                    </div>
                    {
                    notifications.map( opt => 
                        <Menu.Item key={ opt.id } onClick={ _ => handleClick( opt ) } >
                            <span className='header'>
                                <h3 dangerouslySetInnerHTML={ { __html: opt.name } } />
                                { !opt.isRead && <PaperIcon icon='Circle2' collection='ultimate' className='alert' /> }
                            </span>
                        
                            
                            <span className='description'>{ htmlDecode( opt.description ).replace( /<[^>]+>/g, '' ).trim() }</span> { /* dont show the true content here or else it will look like crap - this scrapes the text only from the html */ }
                            <span className='subtitle'>{ moment.utc( opt.publishedAt ).local().startOf( 'seconds' ).fromNow() }</span>
                        </Menu.Item> )
                    }
                </>
            }
        </Menu> ), [ notifications ] );

    return ( <>
            <Dropdown 
                trigger={ [ 'click' ] }
                onClick={ _ => setVisible( !visible ) }
                onVisibleChange={ onVisibleChange }
                visible={ visible }
                overlay={ notiList }
                overlayClassName={ classNames( 'dropdown', 'notification-dropdown', ACTIVE_THEME_NAME ) }
            >
                <a onClick={ e => e.preventDefault() }>
                    { visible ? <BellFull/>  : <BellEmpty/> }
                    { unreadCount > 0 && <PaperIcon icon='Circle2' collection='ultimate' /> }
                </a>
            </Dropdown>
            <NotificationCarousel ref={ carouselRef } options={ { destroyOnClose: false } } onNotiRead={ localRead } loggingOut={ loggingOut } /> 
        </> );
} );

